import styled from 'styled-components';
import { FaMap } from 'react-icons/fa';
import { LuBox } from 'react-icons/lu';

import { useProject } from '@contexts/Project.context';
import useKeyboardShortcut from '@hooks/useKeyboardShortcut.hook';
import { SELECTOR_POSITION } from '../baseMap/constants';

const Container = styled.div`
	position: absolute;
	right: ${SELECTOR_POSITION.right}px;
	bottom: ${SELECTOR_POSITION.default + 40 + 8}px;
	z-index: 19;
	display: flex;
	border-radius: 8px;
	overflow: hidden;
	border: 1px solid rgba(${({ theme }) => theme.colors.borderLightRgb}, 0.1);
	background: ${({ theme }) => theme.colors.background};
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;

const Segment = styled.button`
	display: flex;
	align-items: center;
	gap: 6px;
	height: 40px;
	padding: 0 12px;
	border: 0;
	font-size: 0.85rem;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.15s ease;
	background: ${({ $active, theme }) => ($active ? theme.colors.green + '26' : 'transparent')};
	color: ${({ $active, theme }) => ($active ? theme.colors.green : theme.colors.textColor)};

	&:hover {
		background: ${({ $active, theme }) =>
			$active ? theme.colors.green + '33' : theme.colors.backgroundAccent + '55'};
	}

	& + & {
		border-left: 1px solid ${({ theme }) => theme.colors.border};
	}
`;

/**
 * Map | 3D: switches the project page's map area between the 2D map and the 3D view.
 * Shown only when the project has a 3D view. Keyboard: 3.
 */
const ViewModeToggle = ({ isVisible = true }) => {
	const { view3d, dispatch } = useProject();
	const available = view3d.manifestStatus === 'ready' && !!view3d.manifest;
	const { active } = view3d;

	useKeyboardShortcut('3', available && isVisible, () =>
		dispatch({ type: 'setView3d', payload: { active: !active } })
	);

	if (!available || !isVisible) return null;

	const setActive = value => dispatch({ type: 'setView3d', payload: { active: value } });

	return (
		<Container role="group" aria-label="Map or 3D view">
			<Segment
				type="button"
				$active={!active}
				aria-pressed={!active}
				title="Show the map (3)"
				onClick={() => setActive(false)}>
				<FaMap size={13} />
				Map
			</Segment>
			<Segment
				type="button"
				$active={active}
				aria-pressed={active}
				title="Show the 3D view (3)"
				onClick={() => setActive(true)}>
				<LuBox size={15} />
				3D
			</Segment>
		</Container>
	);
};

export default ViewModeToggle;
